import React, { useState, useEffect, useRef } from 'react';
import { Search, User, LogOut, ChevronDown, Package } from 'lucide-react';
import NotificacionesCampana from './NotificacionesCampana';
import { useNotificaciones } from '../contexts/NotificacionesContext';
import apiService from '../services/api';

function Navbar({ user, onLogout, onClienteClick, onContenedorClick }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [loading, setLoading] = useState(false);

  const searchRef = useRef(null);
  const userMenuRef = useRef(null);

  const { permisoPush, solicitarPermisosPush } = useNotificaciones();

  // Cerrar menús al hacer click afuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Buscar clientes
  useEffect(() => {
    const searchClientes = async () => {
      if (searchTerm.length < 2) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      setLoading(true);
      try {
        const data = await apiService.get(`/search/clientes?q=${encodeURIComponent(searchTerm)}`);
        setSearchResults(data);
        setShowResults(true);
      } catch (error) {
        console.error('Error al buscar:', error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchClientes, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const handleClienteSelect = (clienteNombre) => {
    setSearchTerm('');
    setShowResults(false);
    onClienteClick(clienteNombre);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    onLogout();
  };

  return (
    <nav className="h-16 bg-white border-b border-ink-100 sticky top-0 z-30">
      <div className="h-full px-6 flex items-center justify-between">
        {/* Search */}
        <div className="flex-1 max-w-xl" ref={searchRef}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300" size={18} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => searchResults.length > 0 && setShowResults(true)}
              placeholder="Buscar clientes..."
              className="
                w-full pl-10 pr-4 py-2
                bg-ink-50 border-0 rounded-lg
                text-ink-800 text-sm placeholder-ink-400
                focus:outline-none focus:ring-2 focus:ring-ink-200
                transition-all duration-200
              "
            />

            {/* Search Results */}
            {showResults && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-ink-100 rounded-xl shadow-lifted overflow-hidden z-50 animate-scale-in">
                {loading ? (
                  <div className="p-6 text-center">
                    <div className="w-5 h-5 border-2 border-ink-200 border-t-ink-600 rounded-full animate-spin mx-auto" />
                    <p className="text-xs text-ink-400 mt-2">Buscando...</p>
                  </div>
                ) : searchResults.length > 0 ? (
                  <div className="py-1">
                    {searchResults.map((cliente) => (
                      <button
                        key={cliente.id}
                        onClick={() => handleClienteSelect(cliente.nombre)}
                        className="
                          w-full px-4 py-3
                          hover:bg-ink-50
                          transition-colors duration-150
                          text-left flex items-center justify-between
                          group
                        "
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-ink-100 rounded-lg flex items-center justify-center">
                            <span className="text-ink-600 text-sm font-medium">
                              {cliente.nombre.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-ink-800 group-hover:text-ink-900">
                              {cliente.nombre}
                            </p>
                            <p className="text-xs text-ink-400 flex items-center gap-1">
                              <Package size={10} />
                              {cliente.totalContenedores} contenedores
                            </p>
                          </div>
                        </div>
                        <ChevronDown size={14} className="text-ink-300 -rotate-90" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 text-center">
                    <p className="text-sm text-ink-500">No se encontraron clientes</p>
                    <p className="text-xs text-ink-400 mt-1">Intenta con otro término</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Section */}
        <div className="ml-6 flex items-center gap-3">
          {/* Notifications */}
          <NotificacionesCampana onContenedorClick={onContenedorClick} />

          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="
                flex items-center gap-3 px-3 py-2
                hover:bg-ink-50 rounded-lg
                transition-colors duration-200
              "
            >
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-ink-800">{user.nombre}</p>
                <p className="text-xs text-ink-400">{user.rol === 'admin' ? 'Administrador' : 'Usuario'}</p>
              </div>
              <div className="w-9 h-9 bg-ink-900 rounded-lg flex items-center justify-center">
                <User size={16} className="text-white" />
              </div>
              <ChevronDown
                size={14}
                className={`text-ink-400 transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Dropdown */}
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-ink-100 rounded-xl shadow-lifted py-1 animate-scale-in">
                <div className="px-4 py-3 border-b border-ink-100">
                  <p className="text-sm font-medium text-ink-800">{user.nombre}</p>
                  <p className="text-xs text-ink-400">{user.email}</p>
                  <div className="mt-2">
                    <span className={`
                      inline-flex px-2 py-0.5 rounded text-xs font-medium
                      ${user.rol === 'admin'
                        ? 'bg-ink-100 text-ink-700'
                        : 'bg-ink-50 text-ink-500'
                      }
                    `}>
                      {user.rol === 'admin' ? 'Administrador' : 'Usuario'}
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleLogout}
                  className="
                    w-full px-4 py-3
                    text-left text-sm text-ink-600
                    hover:bg-ink-50
                    transition-colors duration-150
                    flex items-center gap-3
                  "
                >
                  <LogOut size={16} className="text-ink-400" />
                  <span>Cerrar sesión</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
